import { listDrags, type ListMove } from './page-lists';

/**
 * The Reorder lists popup's working copy, pure: what the owner does to the page's lists before Save order, kept as the
 * moves they made, in turn, and the lists they hid or showed. Nothing here touches the page; Save hands the moves and
 * the flags to `withListRows`, which makes the same moves on the page as the drags they were, as one change.
 *
 * The rows the popup shows are never stored: they are the page's rows as they are now with the moves made on them, the
 * same way `withListRows` makes them. So a row the page loses meanwhile (a list deleted from Edit, over the popup)
 * drops out with its moves, one it gains shows where Save order will leave it, and what the popup shows is the order
 * Save order gives.
 */

/** One row as the popup shows it: a page row's key (`PageListRow.key`, what `ReorderListItem.id` carries) and whether
 *  it is hidden. */
export type ReorderRow = { key: string; hidden: boolean };

/** The owner's changes: their moves in the order made, and the flags they set, by row key. */
export type ReorderCopy = { moves: readonly ListMove[]; hidden: ReadonlyMap<string, boolean> };

/** The copy the popup opens with: no changes. */
export const UNTOUCHED_COPY: ReorderCopy = { moves: [], hidden: new Map() };

type ReorderItem = { id: string; hidden: boolean };

/** The owner's moves made on the page's rows as they are now, as `withListRows` makes them on the page. */
const replayed = (copy: ReorderCopy, items: readonly ReorderItem[]): ReturnType<typeof listDrags> =>
  listDrags(
    items.map((item) => item.id),
    copy.moves,
  );

/** The page's rows (`items`, in page order) as the popup shows them: in the order the owner's moves leave them, each
 *  hidden as the owner set it, else as the page has it. */
export const reorderedRows = (copy: ReorderCopy, items: readonly ReorderItem[]): ReorderRow[] => {
  const pageHidden = new Map(items.map((item) => [item.id, item.hidden]));
  return replayed(copy, items).keys.map((key) => ({
    key,
    hidden: copy.hidden.get(key) ?? pageHidden.get(key) ?? false,
  }));
};

/** The copy with one more move: the row at `from` put at `to`, both counted among the rows as the popup shows them
 *  (`reorderedRows`). A move in place or off the rows changes nothing. */
export const withRowMoved = (
  copy: ReorderCopy,
  items: readonly ReorderItem[],
  from: number,
  to: number,
): ReorderCopy => {
  const { keys } = replayed(copy, items);
  if (from === to || from < 0 || to < 0 || from >= keys.length || to >= keys.length) return copy;
  const order = keys.slice();
  order.splice(to, 0, ...order.splice(from, 1));
  return { ...copy, moves: [...copy.moves, { key: keys[from], order }] };
};

/** The copy with the row `key` hidden or shown; it keeps its place. */
export const withRowHidden = (copy: ReorderCopy, key: string, hidden: boolean): ReorderCopy => ({
  ...copy,
  hidden: new Map(copy.hidden).set(key, hidden),
});

export type ReorderChange = 'none' | 'order' | 'hidden';

/**
 * What the copy changes on the page as it is now: nothing; the order (hides too, or not); or hides alone. Any move
 * that still moves a row is a change of order, even one the owner then moved back: Save order makes both as the drags
 * they were, and a list dragged down and back up can come back on the other side of a section that shows no list. A
 * row the page lost meanwhile changes nothing, and one it gained is not a change until the owner hides or shows it.
 */
export const reorderChange = (copy: ReorderCopy, items: readonly ReorderItem[]): ReorderChange => {
  if (replayed(copy, items).drags.length > 0) return 'order';
  const pageHidden = new Map(items.map((item) => [item.id, item.hidden]));
  return reorderedRows(copy, items).some((row) => row.hidden !== pageHidden.get(row.key)) ? 'hidden' : 'none';
};
